/**
 * The record a campaign leaves when it is refused before it ever runs.
 *
 * Her ask, 2026-07-28: error messages have to be exportable too. Until now a
 * refused or rejected campaign produced no `campaign-complete` frame, so
 * `CompletionBlock` rendered nothing and there was no artifact at all — the
 * reviewer could read the reason on screen and then had nothing to hand anyone.
 * That fails her own bar for a narrative, *"could it be rerun from this alone?"*,
 * in the most complete way possible: there was no document.
 *
 * **This is deliberately NOT a narrative.** A refusal measured nothing, so it has
 * no decisions, no evidence, no ranked leads, and it must never borrow the shape of
 * a run that produced results — rule 10, report only what the run measured. What it
 * does carry is everything needed to reconstruct the attempt: the reviewed workflow
 * with its exact parameter values, the masked workspace it was refused against, the
 * autonomy mode, and the refusal in the words the panel showed.
 *
 * Masked by construction: the workspace snapshot arrives masked, and reviewed
 * parameter values carry no identities. Nothing here reads the live workspace.
 *
 * @module gibbs/autonomousCampaign/narrative/refusalRecord
 */

// Mirrors the narrative renderer exactly: `inline` only collapses whitespace, and
// markdown escaping happens later, when the alias layer decodes masked ids for
// display. Escaping here as well would print `PROTEIN\_A` where every other
// campaign artifact prints `PROTEIN_A`.
function inline(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
}

function tableCell(value) {
    return inline(value).replace(/\|/g, '\\|');
}

function formatValue(value) {
    if (value === null || value === undefined) return '_(unset)_';
    if (typeof value === 'object') return `\`${tableCell(JSON.stringify(value))}\``;
    if (value === '') return '_(blank — falls back to the plan value)_';
    return `\`${tableCell(value)}\``;
}

function reviewedParamCell(params) {
    const entries = Object.entries(params || {});
    if (entries.length === 0) return '_(no parameters)_';
    return entries
        .map(([key, value]) => `${tableCell(key)} = ${formatValue(value)}`)
        .join('; ');
}

function workspaceLines(workspace) {
    const lines = [];
    const proteins = Array.isArray(workspace?.proteins) ? workspace.proteins : [];
    const compounds = Array.isArray(workspace?.compounds) ? workspace.compounds : [];
    const binding = workspace?.meta?.workspaceBindingId || workspace?.workspaceBindingId;

    if (proteins.length > 0) {
        const described = proteins
            .map((protein) => {
                const id = inline(protein?.id || '(unmasked)');
                // `role` is only present once more than one structure is loaded,
                // which is exactly the shape most of these refusals are about.
                const role = protein?.role
                    || (protein?.isTarget === true ? 'target' : 'protein');
                return `${id} (${inline(role)})`;
            })
            .join(', ');
        lines.push(`- **Structures loaded:** ${proteins.length} — ${described}`);
    } else {
        lines.push('- **Structures loaded:** none reported');
    }

    lines.push(`- **Compounds loaded:** ${compounds.length}`);

    const fragment = workspace?.fragmentAvailability;
    if (fragment && typeof fragment === 'object') {
        lines.push(
            `- **Fragment availability:** ${inline(fragment.status || 'unknown')}`
            + ` (source ${inline(fragment.source || 'unknown')},`
            + ` ${fragment.entryCount === null || fragment.entryCount === undefined
                ? 'no entry count'
                : `${fragment.entryCount} entries`})`,
        );
    }
    if (binding) lines.push(`- **Workspace binding:** ${inline(binding)}`);
    return lines;
}

/**
 * Render the refusal record as markdown.
 *
 * @param {Object} args
 * @param {Object} args.plan the reviewed campaign plan (already carrying overrides)
 * @param {Object} [args.workspace] the masked workspace snapshot it was refused against
 * @param {string} args.reason the refusal, in the words the panel showed
 * @param {string} [args.autonomyMode]
 * @param {string} [args.at] ISO timestamp; the caller supplies it so this stays pure
 * @param {string} [args.stage] `'review'` (refused before submission) or `'submission'`
 * @returns {string} markdown, or '' when there is nothing to record
 */
export function renderCampaignRefusalRecord({
    plan = null,
    workspace = null,
    reason = '',
    autonomyMode = null,
    at = null,
    stage = 'review',
} = {}) {
    const refusal = String(reason || '').trim();
    if (!refusal) return '';

    const actions = Array.isArray(plan?.actions) ? plan.actions : [];
    const goal = plan?.summary || plan?.workflowId || '(unnamed campaign)';
    const lines = [];

    lines.push(`# Campaign Refusal Record: ${inline(goal)}`);
    lines.push('');
    if (plan?.workflowId) lines.push(`- **Attempted workflow:** ${inline(plan.workflowId)}`);
    if (plan?.source) lines.push(`- **Workflow source:** ${inline(plan.source)}`);
    // `Started:` rather than a bespoke key so the download filename picks the
    // timestamp up the same way a narrative's does.
    if (at) lines.push(`- **Started:** ${inline(at)}`);
    if (autonomyMode) lines.push(`- **Autonomy mode:** ${inline(autonomyMode)}`);
    lines.push(
        `- **Outcome:** REFUSED ${stage === 'submission'
            ? 'AT SUBMISSION'
            : 'BEFORE SUBMISSION'} — no action was dispatched, nothing was computed,`
        + ' and no artifact was exported.',
    );
    lines.push('');

    lines.push('> **Why it was refused**');
    lines.push('>');
    refusal.split('\n').forEach((line) => lines.push(`> ${line.trim()}`));
    lines.push('');

    lines.push('## The workspace it was refused against');
    lines.push('');
    workspaceLines(workspace).forEach((line) => lines.push(line));
    lines.push('');

    lines.push('## The workflow that was reviewed');
    lines.push('');
    if (actions.length === 0) {
        lines.push('_No steps were present on the reviewed plan._');
    } else {
        lines.push('| # | Action | Reviewed values |');
        lines.push('|---|---|---|');
        actions.forEach((action, index) => {
            lines.push(
                `| ${index + 1} | ${tableCell(action?.actionId || '(unknown)')}`
                + ` | ${reviewedParamCell(action?.params)} |`,
            );
        });
    }
    lines.push('');

    lines.push('## Nothing here was measured');
    lines.push('');
    lines.push(
        'This record exists so a refused campaign leaves evidence of what was'
        + ' attempted. The steps above were reviewed but never dispatched, so no'
        + ' value in this document came from a computation. Identities are masked,'
        + ' as they are in every campaign artifact.',
    );
    lines.push('');

    return lines.join('\n');
}
